import { SUPABASE_PUBLISHABLE_KEY, SUPABASE_URL } from './config';

export type AdminAuthProbeResponse = {
  data: boolean | null;
  error: { message: string } | null;
};

/** Probe internal Admin entry, distinct from the broader MFA requirement,
 * with the exact token that caused the auth event.
 *
 * `signal` lets a timed-out caller abort the underlying fetch (PR #354):
 * without it, a hung request stays in-flight after the UI has already moved
 * to the timeout error, stacking pending connections across retries.
 */
export async function probePrivilegedAccess(
  accessToken: string,
  signal?: AbortSignal,
): Promise<AdminAuthProbeResponse> {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/rpc/can_enter_internal_admin`,
    {
      method: 'POST',
      cache: 'no-store',
      signal,
      headers: {
        apikey: SUPABASE_PUBLISHABLE_KEY,
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: '{}',
    },
  );

  const body = await response.text();
  if (!response.ok) {
    let message = `Access verification failed (${response.status})`;
    try {
      const parsed = JSON.parse(body) as { message?: unknown };
      if (typeof parsed.message === 'string' && parsed.message) message = parsed.message;
    } catch {
      // Preserve the status-based message for a non-JSON gateway response.
    }
    return { data: null, error: { message } };
  }

  try {
    const data: unknown = JSON.parse(body);
    if (typeof data !== 'boolean') {
      return { data: null, error: { message: 'Access verification returned an invalid response' } };
    }
    return { data, error: null };
  } catch {
    return { data: null, error: { message: 'Access verification returned invalid JSON' } };
  }
}
